import { useCallback, useEffect, useState } from "react";
import { GraduationCap, CheckCircle2, Lock, XCircle, Clock, ChevronDown, FileText, Image as ImageIcon, Video, Link2, Download, Loader2, FolderOpen } from "lucide-react";
import { toast } from "sonner";
import { api, errText } from "../../connect/api";
import { Header, Spinner, Empty } from "./ui";

const fmt = (d) => new Date(`${d}T00:00:00`).toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long" });
const ICON = { completed: [CheckCircle2, "text-emerald-400"], missed: [XCircle, "text-red-400"], pending: [Clock, "text-slate-500"] };
const kb = (n) => (n > 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);
const blobCache = new Map();

const Material = ({ m }) => {
  const [busy, setBusy] = useState(false);
  const Icon = m.kind === "video" ? Video : m.kind === "link" ? Link2 : m.type?.startsWith("image/") ? ImageIcon : FileText;
  const open = async () => {
    if (m.url) { window.open(m.url, "_blank", "noopener"); api.post(`/training/materials/${m.id}/opened`).catch(() => {}); return; }
    setBusy(true);
    try {
      let u = blobCache.get(m.id);
      if (!u) { const { data } = await api.get(`/training/materials/${m.id}/file`, { responseType: "blob" }); u = URL.createObjectURL(data); blobCache.set(m.id, u); }
      const a = document.createElement("a"); a.href = u; a.target = "_blank"; a.rel = "noopener";
      if (!(m.type === "application/pdf" || m.type?.startsWith("image/"))) a.download = m.name;
      document.body.appendChild(a); a.click(); a.remove();
    } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };
  return (
    <button type="button" onClick={open} disabled={busy} className="nxc-card w-full flex items-center gap-3 px-3 py-2.5 text-left active:scale-[0.99] transition-transform" data-testid={`my-material-${m.id}`}>
      <span className="w-9 h-9 rounded-xl bg-amber-500/15 text-amber-200 flex items-center justify-center shrink-0"><Icon size={17} /></span>
      <span className="min-w-0 flex-1"><span className="block text-sm text-slate-100 truncate">{m.name}</span><span className="block text-[11px] text-slate-500 truncate">{m.kind === "video" ? "Video · opens in a new tab" : m.kind === "link" ? "Link · opens in a new tab" : `${(m.ext || "file").toUpperCase()}${m.size ? ` · ${kb(m.size)}` : ""} · tap to view / download`}</span></span>
      {busy ? <Loader2 size={16} className="animate-spin text-slate-400 shrink-0" /> : <Download size={16} className="text-slate-500 shrink-0" />}
    </button>
  );
};

const Session = ({ day, s, editable, reload }) => {
  const [notes, setNotes] = useState(s.notes || "");
  const [busy, setBusy] = useState(false);
  useEffect(() => setNotes(s.notes || ""), [s.notes]);
  const [Icon, color] = ICON[s.status] || ICON.pending;
  const complete = async () => {
    setBusy(true);
    try { await api.post("/training/complete", { date: day.date, session: s.key }); toast.success(`${s.label} session marked complete`); reload(); } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };
  const saveNotes = async () => {
    if (notes === (s.notes || "")) return;
    setBusy(true);
    try { await api.put("/training/notes", { date: day.date, session: s.key, notes }); toast.success("Notes saved"); reload(); } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };
  return (
    <div className="nxc-card p-3.5" data-testid={`my-session-${day.date}-${s.key}`}>
      <div className="flex items-start gap-2.5">
        <Icon size={18} className={`${color} mt-0.5 shrink-0`} />
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500">{s.label}{s.completed_at ? ` · done ${s.completed_at.slice(11, 16)}` : s.status === "missed" ? " · missed" : ""}</div>
          {s.topic && <p className="text-sm text-slate-100 mt-0.5 break-words">{s.topic}</p>}
        </div>
        {editable && s.status !== "completed" && (
          <button disabled={busy} onClick={complete} className="shrink-0 rounded-full bg-emerald-500/15 border border-emerald-400/40 text-emerald-200 text-xs font-semibold px-3 py-1.5 active:scale-95 transition-transform disabled:opacity-50" data-testid={`mark-complete-${day.date}-${s.key}`}>Mark Complete</button>
        )}
      </div>
      {editable ? (
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} onBlur={saveNotes} rows={3} maxLength={4000} placeholder="Notes — what you learned, questions for review…" className="nxc-input mt-3 resize-y text-[13px]" data-testid={`notes-${day.date}-${s.key}`} />
      ) : (
        <p className="mt-3 text-[13px] text-slate-400 whitespace-pre-wrap break-words border-t border-white/5 pt-2" data-testid={`notes-ro-${day.date}-${s.key}`}>{s.notes || <span className="italic text-slate-600">No notes written</span>}</p>
      )}
    </div>
  );
};

/** Per-day material: clickable only on that calendar date; greyed out before/after or when no file. */
const DayMaterial = ({ d, today }) => {
  const [busy, setBusy] = useState(false);
  const m = d.material;
  const active = !!m && d.date === today;
  const open = async (e) => {
    e.stopPropagation();
    if (!active) return;
    setBusy(true);
    try {
      let u = blobCache.get(`day-${d.date}`);
      if (!u) { const { data } = await api.get(`/training/days/${d.date}/material/file`, { responseType: "blob" }); u = URL.createObjectURL(data); blobCache.set(`day-${d.date}`, u); }
      const a = document.createElement("a"); a.href = u; a.target = "_blank"; a.rel = "noopener";
      if (!(m.type === "application/pdf")) a.download = m.name;
      document.body.appendChild(a); a.click(); a.remove();
    } catch (err) { toast.error(errText(err)); } finally { setBusy(false); }
  };
  const hint = !m ? "No document for this day" : active ? `${m.name}${m.size ? ` · ${kb(m.size)}` : ""}` : d.date < today ? `Was available on ${fmt(d.date).split(",")[0]} ${d.date.slice(8)} only` : `Unlocks on ${fmt(d.date)}`;
  const v = d.video;
  const vActive = !!v && d.date === today;
  const openVideo = (e) => {
    e.stopPropagation();
    if (!vActive) return;
    window.open(v.url, "_blank", "noopener");
    api.post(`/training/days/${d.date}/video/opened`).catch(() => {});
  };
  const vHint = !v ? "No video for this day" : vActive ? (v.title && v.title !== "YouTube video" && v.title !== "Video link" ? v.title : v.host || v.url) : d.date < today ? `Was available on ${fmt(d.date).split(",")[0]} ${d.date.slice(8)} only` : `Unlocks on ${fmt(d.date)}`;
  if (!m && !v) return null;
  return (
    <div className="mx-3.5 mb-3 flex flex-col gap-2" data-testid={`day-materials-${d.date}`}>
      {m && (
        <div className={`rounded-xl border px-3 py-2.5 flex items-center gap-3 ${active ? "border-amber-400/40 bg-amber-500/10" : "border-white/5 bg-white/[0.02]"}`} data-testid={`day-material-${d.date}`}>
          <span className="min-w-0 flex-1"><span className="block text-xs text-slate-300">Training document</span><span className={`block text-[11px] truncate ${active ? "text-amber-100" : "text-slate-500"}`} data-testid={`day-material-hint-${d.date}`}>{hint}</span></span>
          <button type="button" onClick={open} disabled={!active || busy} aria-disabled={!active} className={`shrink-0 inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-semibold transition-colors ${active ? "bg-amber-400 text-[#2a1b00] hover:bg-amber-300" : "bg-white/5 text-slate-500 cursor-not-allowed"}`} data-testid={`day-material-download-${d.date}`}>
            {busy ? <Loader2 size={14} className="animate-spin" /> : active ? <Download size={14} /> : <Lock size={13} />} Download
          </button>
        </div>
      )}
      {v && (
        <div className={`rounded-xl border px-3 py-2.5 flex items-center gap-3 ${vActive ? "border-red-400/40 bg-red-500/10" : "border-white/5 bg-white/[0.02]"}`} data-testid={`day-video-${d.date}`}>
          <span className="min-w-0 flex-1"><span className="block text-xs text-slate-300">{v.kind === "youtube" ? "YouTube video" : "Training video"}</span><span className={`block text-[11px] truncate ${vActive ? "text-red-100" : "text-slate-500"}`} data-testid={`day-video-hint-${d.date}`}>{vHint}</span></span>
          <button type="button" onClick={openVideo} disabled={!vActive} aria-disabled={!vActive} className={`shrink-0 inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-semibold transition-colors ${vActive ? "bg-red-500 text-white hover:bg-red-400" : "bg-white/5 text-slate-500 cursor-not-allowed"}`} data-testid={`day-video-open-${d.date}`}>
            {vActive ? <Video size={14} /> : <Lock size={13} />} Watch
          </button>
        </div>
      )}
    </div>
  );
};

const Day = ({ d, editable, reload, defaultOpen, today }) => {
  const [open, setOpen] = useState(defaultOpen);
  const done = d.sessions.filter((s) => s.status === "completed").length;
  return (
    <div className={`rounded-2xl border ${d.is_today ? "border-amber-400/40" : "border-white/5"}`} data-testid={`my-day-${d.date}`}>
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center gap-3 px-3.5 py-3 text-left" data-testid={`my-day-toggle-${d.date}`}>
        <div className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${d.day_status === "completed" ? "bg-emerald-500/20 text-emerald-200" : d.day_status === "missed" ? "bg-red-500/15 text-red-200" : "bg-white/5 text-slate-300"}`}>D{d.day}</div>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-slate-100 truncate">{fmt(d.date)}{d.is_today && <span className="ml-2 text-[10px] uppercase tracking-wider text-amber-300">Today</span>}</div>
          <div className="text-xs text-slate-400">{done}/{d.sessions.length} sessions completed</div>
        </div>
        <ChevronDown size={16} className={`text-slate-500 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      <DayMaterial d={d} today={today} />
      {open && <div className="px-3 pb-3 flex flex-col gap-2">{d.sessions.map((s) => <Session key={s.key} day={d} s={s} editable={editable && d.is_today} reload={reload} />)}</div>}
    </div>
  );
};

export const MyTraining = () => {
  const [v, setV] = useState();
  const load = useCallback(() => api.get("/training/me").then(({ data }) => setV(data)).catch(() => setV(null)), []);
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, [load]);
  if (v === undefined) return <div className="h-full flex items-center justify-center"><Spinner /></div>;
  if (!v) return <div className="h-full flex flex-col"><Header title="My Training" /><Empty icon={GraduationCap} title="No training assigned" /></div>;
  const st = v.stats;
  const today = v.today_plan;
  return (
    <div className="h-full flex flex-col" data-testid="my-training">
      <Header title="My Training" sub={v.erp_company ? `${v.title} · ${v.erp_company}` : v.title} />
      <div className="flex-1 overflow-y-auto p-3 pb-6 flex flex-col gap-3 nxc-fade-in">
        <div className="nxc-card p-4" data-testid="my-training-progress">
          <div className="flex items-center justify-between gap-3">
            <div><div className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Progress</div><div className="text-2xl font-semibold text-slate-100 nxc-display">{st.pct}%</div></div>
            <div className="text-right text-xs text-slate-400"><div>{st.sessions_done}/{st.sessions_total} sessions</div><div>{st.days_done}/{st.days_total} days completed</div></div>
          </div>
          <div className="h-2.5 rounded-full bg-white/10 overflow-hidden mt-3"><div className="h-full rounded-full bg-gradient-to-r from-amber-400 to-emerald-400 transition-[width] duration-700" style={{ width: `${st.pct}%` }} /></div>
          <div className="flex gap-1 mt-2">{v.days.map((d) => <div key={d.date} title={d.date} className={`flex-1 h-1.5 rounded-full ${d.day_status === "completed" ? "bg-emerald-400" : d.day_status === "missed" ? "bg-red-400/70" : d.is_today ? "bg-amber-400" : "bg-white/10"}`} />)}</div>
        </div>
        {today ? (
          <div className="rounded-2xl border border-amber-400/30 bg-amber-500/[0.05] p-3.5" data-testid="my-training-today">
            <div className="flex items-center gap-2 text-amber-200 text-[10px] uppercase tracking-[0.2em]"><GraduationCap size={13} /> Today · Day {today.day} · {fmt(today.date)}</div>
            {v.editable ? <p className="text-xs text-slate-300 mt-1">Mark each session complete and write your notes before <b>{v.cutoff}</b>.</p>
              : <p className="text-xs text-amber-200/90 mt-1 inline-flex items-center gap-1.5" data-testid="my-training-readonly"><Lock size={12} /> Read-only after {v.cutoff} — you can review today's sessions and notes.</p>}
            <div className="mt-3 -mx-3.5"><DayMaterial d={today} today={v.today} /></div>
            <div className="flex flex-col gap-2">{today.sessions.map((s) => <Session key={s.key} day={today} s={s} editable={v.editable} reload={load} />)}</div>
          </div>
        ) : (
          <div className="nxc-card p-3.5 text-sm text-slate-400" data-testid="my-training-no-session-today">{v.today < v.start ? `Training starts ${fmt(v.start)}.` : v.today > v.end ? "Training completed. Thank you!" : "No training session scheduled today."}</div>
        )}
        {(v.materials || []).length > 0 && (
          <div data-testid="my-training-materials">
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mt-1 px-1 flex items-center gap-1.5"><FolderOpen size={12} /> Training materials · {v.materials.length}</div>
            <div className="flex flex-col gap-1.5 mt-2">{v.materials.map((m) => <Material key={m.id} m={m} />)}</div>
          </div>
        )}
        <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mt-1 px-1">Full schedule</div>
        {v.days.filter((d) => !d.is_today).map((d) => <Day key={d.date} d={d} today={v.today} editable={false} reload={load} defaultOpen={false} />)}
      </div>
    </div>
  );
};
